import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { useReveal } from '@/hooks/use-reveal'
import { Media } from '@/components/generals/media'
import { Eyebrow } from '@/components/generals/section-heading'
import { SplitWords } from '@/components/generals/split-words'
import { Viewfinder } from '@/components/generals/viewfinder'
import { numbers, numbersContent } from '@/constants/content'
import { media } from '@/constants/media'

/** A few key figures with a sticky photo, counted up as they scroll into view. */
export const Numbers: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useReveal(scope)

    return (
        <section ref={scope} id='numbers' aria-labelledby='numbers-title' className='relative bg-bone py-28 text-ink md:py-40'>
            <div className='gutter'>
                <div className='flex flex-col gap-10 md:flex-row md:items-end md:justify-between'>
                    <div className='flex flex-col gap-8'>
                        <Eyebrow index='04' className='text-stone-deep'>
                            {numbersContent.label}
                        </Eyebrow>

                        <h2 id='numbers-title' className='font-wide text-huge font-semibold uppercase tracking-[-0.05em]' data-reveal='words'>
                            <SplitWords text={numbersContent.title} />
                            <br />
                            <SplitWords text={numbersContent.subtitle} wordClassName='font-serif font-normal normal-case italic tracking-[-0.02em]' />
                        </h2>
                    </div>

                    <p className='max-w-sm text-lead text-ink/75' data-reveal='fade'>
                        {numbersContent.description}
                    </p>
                </div>

                <div className='mt-20 grid gap-12 md:mt-28 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='flex flex-col gap-6 lg:sticky lg:top-28'>
                            <Media
                                src={media.teaching.src}
                                alt={media.teaching.alt}
                                sizes='(min-width: 1024px) 38vw, 92vw'
                                className='aspect-[4/5]'
                                reveal
                                parallax={8}
                            >
                                <Viewfinder />
                                <span className='eyebrow absolute left-6 top-6 z-10 text-bone'>
                                    {numbersContent.imageLabel}
                                </span>
                                <span className='absolute bottom-6 left-6 z-10 flex flex-col text-bone'>
                                    <span className='font-wide text-6xl font-semibold leading-none tracking-[-0.05em]'>
                                        {numbersContent.imageMetric}
                                    </span>
                                    <span className='eyebrow mt-2'>
                                        {numbersContent.imageCaption}
                                    </span>
                                </span>
                            </Media>

                            <p className='max-w-sm text-sm leading-relaxed text-ink/70'>
                                {numbersContent.note}
                            </p>
                        </div>
                    </div>

                    <ol className='flex flex-col lg:col-span-7'>
                        {numbers.map((item, index) => (
                            <li key={item.label} className='grid gap-8 border-t border-ink/15 py-12 first:border-t-0 first:pt-0 md:grid-cols-7 md:py-16'>
                                <div className='flex flex-col gap-3 md:col-span-3'>
                                    <span className='eyebrow text-stone-deep'>
                                        0{index + 1}
                                    </span>

                                    <p className='font-wide text-[clamp(4rem,9vw,8.5rem)] font-semibold leading-[0.82] tracking-[-0.06em]'>
                                        <span data-count={item.value}>{item.value}</span>
                                        <span className='text-flare'>{item.suffix}</span>
                                    </p>
                                </div>

                                <div className='flex flex-col justify-end gap-4 md:col-span-4' data-reveal='fade'>
                                    <h3 className='font-wide text-2xl font-semibold uppercase leading-none tracking-[-0.035em] md:text-3xl'>
                                        {item.label}
                                    </h3>

                                    <p className='font-serif text-[clamp(1.5rem,2.4vw,2.25rem)] italic leading-[1.1] tracking-[-0.01em] text-ink/85'>
                                        {item.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    )
}
